import React, { useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Spinner } from 'reactstrap';
import styled from '@emotion/styled';
import { Button } from '../../Components/Button';
import { useTrans } from '../../Store/System/LocalizationStore';
import { Theme } from '../../Theme/Theme';
import { HttpBackend } from '../../Services/Http/HttpBackend';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Description = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin-bottom: 24px;
  line-height: 1.5;
`;

const StyledInput = styled(Input)`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 15px;
  border: 1px solid ${Theme.Colors.lighterGrey};

  &:focus {
    border-color: ${Theme.Colors.primary};
    box-shadow: 0 0 0 3px ${Theme.Colors.primary}20;
  }
`;

const SuccessMessage = styled.div`
  background: ${Theme.Colors.success}15;
  border: 1px solid ${Theme.Colors.success};
  border-radius: 8px;
  padding: 16px;
  color: ${Theme.Colors.success};
  text-align: center;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 8px;
  padding: 12px 16px;
  color: #dc2626;
  margin-bottom: 16px;
  font-size: 14px;
`;

export type ChangeEmailModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const ChangeEmailModal: React.FC<ChangeEmailModalProps> = observer(({ isOpen, onClose }) => {
  const { t } = useTrans();

  const [newEmail, setNewEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isEmailValid = EMAIL_REGEX.test(newEmail);
  const showEmailError = newEmail.length > 0 && !isEmailValid;

  const handleClose = useCallback(() => {
    setNewEmail('');
    setError(null);
    setSuccess(false);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEmailValid) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await HttpBackend.post('/uaa/profile/change-email', {
        email: newEmail,
        callbackUrl: `${window.location.origin}/auth/callback`,
      });
      setSuccess(true);
    } catch (err: any) {
      console.error('Error initiating email change:', err);
      setError(
        err?.message ||
        t('profile.email.changeRequestErrorMessage', {
          defaultValue: 'An error occurred while trying to change your email. Please try again.',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [newEmail, isEmailValid, t]);

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        {t('profile.email.changeEmailTitle', { defaultValue: 'Change Email' })}
      </ModalHeader>
      <ModalBody>
        {success ? (
          <SuccessMessage>
            <i className="bi bi-check-circle" style={{ fontSize: 32, marginBottom: 12, display: 'block' }} />
            <strong style={{ display: 'block', marginBottom: 8 }}>
              {t('profile.email.changeRequestSuccessTitle', { defaultValue: 'Verification Email Sent' })}
            </strong>
            {t('profile.email.changeEmailRequestSuccessMessage', {
              defaultValue: `Please check your new email address (${newEmail}) and click the verification link to confirm the change.`,
            })}
          </SuccessMessage>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Description>
              {t('profile.email.description', {
                defaultValue: "Enter your new email address. We'll send you a verification link to confirm the change.",
              })}
            </Description>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <FormGroup>
              <Label for="newEmail">
                {t('profile.email.newEmailLabel', { defaultValue: 'New Email' })}
              </Label>
              <StyledInput
                type="email"
                id="newEmail"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={t('profile.email.emailPlaceholder', { defaultValue: 'Enter your new email address' })}
                invalid={showEmailError}
                autoFocus
              />
              {showEmailError && (
                <FormFeedback>
                  {t('profile.email.invalidEmailError', { defaultValue: 'Please enter a valid email address' })}
                </FormFeedback>
              )}
            </FormGroup>
          </Form>
        )}
      </ModalBody>
      <ModalFooter>
        {success ? (
          <Button color="primary" onClick={handleClose}>
            {t('globals.done', { defaultValue: 'Done' })}
          </Button>
        ) : (
          <>
            <Button color="secondary" onClick={handleClose} disabled={isSubmitting}>
              {t('globals.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button color="primary" onClick={handleSubmit} disabled={!isEmailValid || isSubmitting}>
              {isSubmitting ? (
                <Spinner size="sm" />
              ) : (
                t('profile.email.submitChangeButton', { defaultValue: 'Send Verification Email' })
              )}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
});
